import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';
import { formatCurrency, getMonthName, calculateEmployeePayroll } from '../lib/payrollEngine';
import type { Payroll } from '../lib/supabase';

const STATUS_LABELS: Record<string, string> = {
  draft: 'Borrador', processing: 'Procesando', approved: 'Aprobado', paid: 'Pagado',
};
const STATUS_BADGE: Record<string, string> = {
  draft: 'badge-warning', processing: 'badge-info', approved: 'badge-neutral', paid: 'badge-success',
};

export function PayrollPage() {
  const { company } = useAuth();
  const navigate = useNavigate();
  const [payrolls, setPayrolls] = useState<Payroll[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [showNewModal, setShowNewModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<Payroll | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [newPeriod, setNewPeriod] = useState({ year: new Date().getFullYear(), month: new Date().getMonth() + 1 });
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
  const [progress, setProgress] = useState({ current: 0, total: 0, name: '' });

  useEffect(() => { if (company) loadPayrolls(); }, [company]);

  function showToast(msg: string, type: 'success' | 'error' = 'success') {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  }

  async function loadPayrolls() {
    setLoading(true);
    const { data } = await supabase
      .from('payrolls')
      .select('*')
      .eq('company_id', company!.id)
      .order('period_year', { ascending: false })
      .order('period_month', { ascending: false });
    setPayrolls(data || []);
    setLoading(false);
  }

  // ── Eliminar planilla ─────────────────────────────────────────────────────
  async function handleDelete() {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      // Primero eliminar los items de la planilla
      await supabase.from('payroll_items').delete().eq('payroll_id', deleteConfirm.id);
      // Luego eliminar la planilla
      const { error } = await supabase.from('payrolls').delete().eq('id', deleteConfirm.id);
      if (error) throw error;
      showToast(`Planilla ${getMonthName(deleteConfirm.period_month)} ${deleteConfirm.period_year} eliminada`);
      setDeleteConfirm(null);
      loadPayrolls();
    } catch (err: any) {
      showToast(err.message || 'Error al eliminar', 'error');
    } finally {
      setDeleting(false);
    }
  }

  // ── Procesar planilla ─────────────────────────────────────────────────────
  async function processPayroll() {
    setProcessing(true);
    setShowNewModal(false);

    try {
      const { data: existing } = await supabase
        .from('payrolls')
        .select('id, status')
        .eq('company_id', company!.id)
        .eq('period_year', newPeriod.year)
        .eq('period_month', newPeriod.month)
        .single();

      if (existing && existing.status !== 'draft') {
        showToast('Ya existe una planilla aprobada para este período. Elimínala primero para reprocessar.', 'error');
        setProcessing(false);
        return;
      }

      const { data: employees } = await supabase
        .from('employees')
        .select('*')
        .eq('company_id', company!.id)
        .eq('active', true);

      if (!employees || employees.length === 0) {
        showToast('No hay empleados activos para procesar', 'error');
        setProcessing(false);
        return;
      }

      const { data: bonuses } = await supabase
        .from('bonuses')
        .select('*')
        .eq('company_id', company!.id)
        .eq('is_active', true);

      let payrollId = existing?.id;
      const periodStart = `${newPeriod.year}-${String(newPeriod.month).padStart(2, '0')}-01`;
      const periodEnd = `${newPeriod.year}-${String(newPeriod.month + 1).padStart(2, '0')}-01`;

      if (!payrollId) {
        const { data: newPayroll, error } = await supabase
          .from('payrolls')
          .insert({
            company_id: company!.id,
            period_year: newPeriod.year,
            period_month: newPeriod.month,
            status: 'processing',
          })
          .select()
          .single();
        if (error) throw error;
        payrollId = newPayroll.id;
      } else {
        await supabase.from('payrolls').update({ status: 'processing' }).eq('id', payrollId);
        await supabase.from('payroll_items').delete().eq('payroll_id', payrollId);
      }

      setProgress({ current: 0, total: employees.length, name: '' });
      const workingDays = company!.working_days_per_month;
      let totalGross = 0, totalDeductions = 0, totalNet = 0, totalEssalud = 0;
      const items = [];

      for (let i = 0; i < employees.length; i++) {
        const emp = employees[i];
        setProgress({ current: i + 1, total: employees.length, name: `${emp.first_name} ${emp.last_name}` });

        const { data: attendance } = await supabase
          .from('attendance')
          .select('*')
          .eq('company_id', company!.id)
          .eq('employee_id', emp.id)
          .gte('date', periodStart)
          .lt('date', periodEnd);

        const result = calculateEmployeePayroll({
          employee: emp,
          company: company!,
          periodYear: newPeriod.year,
          periodMonth: newPeriod.month,
          attendanceRecords: attendance || [],
          bonuses: bonuses || [],
          workingDaysInMonth: workingDays,
        });

        totalGross += result.grossSalary;
        totalDeductions += result.totalDeductions;
        totalNet += result.netSalary;
        totalEssalud += result.essaludAmount;

        items.push({
          payroll_id: payrollId,
          company_id: company!.id,
          employee_id: emp.id,
          base_salary: result.baseSalary,
          working_days: workingDays,
          days_worked: result.daysWorked,
          overtime_hours: result.overtimeHours,
          overtime_amount: result.overtimeAmount,
          family_assignment: result.familyAssignment,
          gratification: result.gratification,
          bonuses_total: result.bonusesTotal,
          other_income: result.bonificacionExtraordinaria,
          gross_salary: result.grossSalary,
          absence_deduction: result.absenceDeduction,
          tardiness_deduction: result.tardinessDeduction,
          absent_days: result.absentDays,
          late_minutes_total: result.lateMinutesTotal,
          onp_amount: result.onpAmount,
          afp_amount: result.afpAmount,
          income_tax: result.incomeTax,
          other_deductions: 0,
          essalud_amount: result.essaludAmount,
          cts_provision: result.ctsProvision,
          total_deductions: result.totalDeductions,
          net_salary: result.netSalary,
          regime_applied: result.regimeApplied,
          calculation_notes: result.calculationNotes,
        });
      }

      const { error: itemsError } = await supabase.from('payroll_items').insert(items);
      if (itemsError) throw itemsError;

      await supabase.from('payrolls').update({
        status: 'approved',
        total_gross: Math.round(totalGross * 100) / 100,
        total_deductions: Math.round(totalDeductions * 100) / 100,
        total_net: Math.round(totalNet * 100) / 100,
        total_essalud: Math.round(totalEssalud * 100) / 100,
        processed_at: new Date().toISOString(),
      }).eq('id', payrollId);

      showToast(`Planilla procesada: ${employees.length} empleados · ${formatCurrency(totalNet)} neto total`);
      loadPayrolls();
    } catch (err: any) {
      showToast(err.message || 'Error al procesar la planilla', 'error');
    } finally {
      setProcessing(false);
      setProgress({ current: 0, total: 0, name: '' });
    }
  }

  const months = Array.from({ length: 12 }, (_, i) => ({ value: i + 1, label: getMonthName(i + 1) }));
  const years = [new Date().getFullYear(), new Date().getFullYear() - 1];

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-8">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-6 right-6 z-50 animate-fade-in flex items-center gap-3 px-5 py-3 rounded-xl shadow-xl text-white text-sm font-bold ${toast.type === 'success' ? 'bg-green-600' : 'bg-error'}`}>
          <span className="material-symbols-outlined text-[18px]">{toast.type === 'success' ? 'check_circle' : 'error'}</span>
          {toast.msg}
        </div>
      )}

      {/* Processing overlay */}
      {processing && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-surface-container-lowest rounded-2xl p-8 max-w-sm w-full mx-4 shadow-2xl text-center animate-fade-in">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <h3 className="font-headline font-bold text-xl text-on-surface mb-2">Procesando planilla</h3>
            {progress.total > 0 && (
              <>
                <p className="text-sm text-on-surface-variant mb-1">{progress.current} / {progress.total} empleados</p>
                <p className="text-xs text-primary font-medium truncate mb-3">{progress.name}</p>
                <div className="w-full bg-surface-container-high rounded-full h-2">
                  <div className="bg-primary h-2 rounded-full transition-all duration-300" style={{ width: `${(progress.current / progress.total) * 100}%` }} />
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-4xl font-extrabold tracking-tight text-on-surface font-headline">Planillas</h1>
          <p className="text-on-surface-variant mt-1">Procesamiento y gestión de planillas mensuales</p>
        </div>
        <button onClick={() => setShowNewModal(true)} className="btn-primary text-white px-6 py-3 rounded-xl font-bold text-sm shadow-lg shadow-primary/20 flex items-center gap-2 hover:opacity-90 active:scale-[0.98] transition-all">
          <span className="material-symbols-outlined text-[20px]">add</span>
          Nueva planilla
        </button>
      </div>

      {/* Payrolls list */}
      <div className="card overflow-hidden">
        <div className="p-5 border-b border-outline-variant/10">
          <h3 className="font-bold text-on-surface font-headline">Historial de planillas</h3>
        </div>

        {loading ? (
          <div className="py-12 flex items-center justify-center">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : payrolls.length === 0 ? (
          <div className="py-16 text-center">
            <span className="material-symbols-outlined text-4xl text-outline-variant">receipt_long</span>
            <p className="text-on-surface-variant text-sm mt-2">No hay planillas procesadas aún</p>
            <button onClick={() => setShowNewModal(true)} className="mt-4 text-primary text-sm font-bold hover:underline">+ Procesar primera planilla</button>
          </div>
        ) : (
          <table className="w-full text-left">
            <thead>
              <tr className="bg-surface-container-low">
                {['Período', 'Estado', 'Total Bruto', 'Deducciones', 'Neto Total', 'EsSalud', 'Acciones'].map(h => (
                  <th key={h} className="px-6 py-4 text-[10px] font-bold text-on-surface-variant uppercase tracking-widest">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant/10">
              {payrolls.map(p => (
                <tr key={p.id} className="hover:bg-surface-container-low/30 transition-colors group">
                  <td className="px-6 py-5 cursor-pointer" onClick={() => navigate(`/payroll/${p.id}`)}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <span className="material-symbols-outlined text-primary text-[20px]">calendar_month</span>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-on-surface">{getMonthName(p.period_month)} {p.period_year}</p>
                        <p className="text-xs text-on-surface-variant">
                          {p.processed_at ? `Procesado ${new Date(p.processed_at).toLocaleDateString('es-PE')}` : 'Sin procesar'}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-5">
                    <span className={STATUS_BADGE[p.status]}>{STATUS_LABELS[p.status]}</span>
                  </td>
                  <td className="px-6 py-5 text-sm font-semibold tabular-nums">{formatCurrency(p.total_gross)}</td>
                  <td className="px-6 py-5 text-sm font-semibold text-error tabular-nums">({formatCurrency(p.total_deductions)})</td>
                  <td className="px-6 py-5 text-sm font-bold text-primary tabular-nums">{formatCurrency(p.total_net)}</td>
                  <td className="px-6 py-5 text-sm tabular-nums text-on-surface-variant">{formatCurrency(p.total_essalud)}</td>
                  <td className="px-6 py-5">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => navigate(`/payroll/${p.id}`)}
                        className="p-2 text-on-surface-variant hover:text-primary hover:bg-surface-container-high rounded-lg transition-all"
                        title="Ver detalle"
                      >
                        <span className="material-symbols-outlined text-[18px]">visibility</span>
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(p)}
                        className="p-2 text-on-surface-variant hover:text-error hover:bg-error-container/30 rounded-lg transition-all"
                        title="Eliminar planilla"
                      >
                        <span className="material-symbols-outlined text-[18px]">delete</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* New payroll modal */}
      {showNewModal && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-surface-container-lowest rounded-2xl shadow-2xl w-full max-w-md animate-fade-in">
            <div className="flex items-center justify-between p-6 border-b border-outline-variant/10">
              <h2 className="text-xl font-bold font-headline">Procesar nueva planilla</h2>
              <button onClick={() => setShowNewModal(false)} className="p-2 hover:bg-surface-container-high rounded-lg transition-colors">
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <div className="p-6 space-y-5">
              <div className="p-4 bg-primary/5 rounded-xl border border-primary/15 text-sm text-on-surface-variant">
                <p className="font-bold text-on-surface mb-1">Cálculo automático según régimen</p>
                <p>El salario se paga <strong>proporcional a los días trabajados</strong> registrados en asistencia. Si no hay registros, se asume mes completo.</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-on-surface-variant uppercase tracking-wider">Mes</label>
                  <select className="input-base" value={newPeriod.month} onChange={e => setNewPeriod(p => ({ ...p, month: +e.target.value }))}>
                    {months.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-on-surface-variant uppercase tracking-wider">Año</label>
                  <select className="input-base" value={newPeriod.year} onChange={e => setNewPeriod(p => ({ ...p, year: +e.target.value }))}>
                    {years.map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
              </div>
            </div>
            <div className="flex gap-3 p-6 border-t border-outline-variant/10">
              <button onClick={() => setShowNewModal(false)} className="flex-1 px-4 py-2.5 rounded-lg border border-outline-variant/30 text-sm font-bold text-on-surface-variant hover:bg-surface-container-low transition-all">Cancelar</button>
              <button onClick={processPayroll} className="flex-1 btn-primary text-white py-2.5 rounded-lg font-bold text-sm shadow-md hover:opacity-90 transition-all flex items-center justify-center gap-2">
                <span className="material-symbols-outlined text-[18px]">send</span>
                Procesar planilla
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-surface-container-lowest rounded-2xl shadow-2xl w-full max-w-sm animate-fade-in">
            <div className="p-6">
              <div className="w-12 h-12 rounded-full bg-error-container flex items-center justify-center mx-auto mb-4">
                <span className="material-symbols-outlined text-error text-2xl">delete_forever</span>
              </div>
              <h2 className="text-xl font-bold font-headline text-center mb-2">¿Eliminar planilla?</h2>
              <p className="text-sm text-on-surface-variant text-center">
                Se eliminará la planilla de <strong>{getMonthName(deleteConfirm.period_month)} {deleteConfirm.period_year}</strong> y todos sus cálculos. Esta acción no se puede deshacer.
              </p>
              <div className="mt-2 p-3 bg-error-container/30 rounded-xl text-center">
                <p className="text-sm font-bold text-error">{formatCurrency(deleteConfirm.total_net)} neto</p>
                <p className="text-xs text-on-surface-variant">{STATUS_LABELS[deleteConfirm.status]}</p>
              </div>
            </div>
            <div className="flex gap-3 p-6 pt-0">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 px-4 py-2.5 rounded-lg border border-outline-variant/30 text-sm font-bold text-on-surface-variant hover:bg-surface-container-low transition-all">
                Cancelar
              </button>
              <button onClick={handleDelete} disabled={deleting} className="flex-1 bg-error text-white py-2.5 rounded-lg font-bold text-sm shadow-md hover:opacity-90 transition-all disabled:opacity-60 flex items-center justify-center gap-2">
                {deleting ? (
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <><span className="material-symbols-outlined text-[18px]">delete</span>Eliminar</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
